import { Link } from 'react-router-dom';
import { formatPrice, formatPriceRange } from '../lib/format';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowUpRight, TrendingUp, Users, MapPin, Clock, Shield, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getNeighbourhoods, trackEvent } from '../lib/api';
import SEO from '../components/SEO';

function FloatingCard({ className, children, delay = '0s' }) {
  return (
    <div className={`glass-card-dark rounded-2xl p-4 animate-fade-up ${className}`} style={{ animationDelay: delay }}>
      {children}
    </div>
  );
}

function ScoreRing({ score, size = 48 }) {
  const radius = (size - 6) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  const color = score >= 70 ? '#0ea58f' : score >= 40 ? '#eab308' : '#ef4444';
  return (
    <div className="score-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
      </svg>
      <span className="absolute font-mono text-xs font-medium text-white">{score}</span>
    </div>
  );
}

export default function HomePage() {
  const [neighbourhoods, setNeighbourhoods] = useState([]);

  useEffect(() => {
    trackEvent({ event_type: 'page_view', page: 'home' });
    getNeighbourhoods().then(r => setNeighbourhoods(r.data.neighbourhoods || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="overflow-hidden">
      <SEO
        title="Find Your Right Neighbourhood in Burlington, Ontario"
        description="Stop scrolling listings. Get matched with Burlington neighbourhoods that fit your budget, school priorities, and family needs. Free Buyer Blueprint — results in under 3 minutes."
        canonical="/"
      />
      {/* ─── Hero ─── */}
      <section className="mesh-gradient-hero noise-overlay relative min-h-[100vh] flex items-center" data-testid="hero-section">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/8 border border-white/10 rounded-full px-4 py-1.5 mb-8 animate-fade-up">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                <span className="text-white/60 text-xs font-medium tracking-wide">Burlington, Ontario</span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-[72px] font-normal leading-[1.05] tracking-tight text-white mb-6 animate-fade-up delay-100" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Stop scrolling listings.
                <br />
                <span className="italic gradient-text">Start here.</span>
              </h1>

              <p className="text-lg text-white/50 leading-relaxed max-w-md mb-10 animate-fade-up delay-200">
                Stop wandering aimlessly through listings. Get a tailored overview of the Burlington neighbourhoods, price expectations, and schools that fit your priorities and budget — instantly.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 animate-fade-up delay-300">
                <Link to="/blueprint" data-testid="hero-cta-blueprint">
                  <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-7 py-3 h-auto text-sm font-semibold shadow-xl shadow-white/10 hover:-translate-y-px transition-all">
                    Find my neighbourhoods <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/neighbourhoods" data-testid="hero-cta-explore">
                  <Button variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10 rounded-full px-6 py-3 h-auto text-sm font-medium">
                    See how it works <ArrowUpRight className="ml-1 w-3.5 h-3.5" />
                  </Button>
                </Link>
              </div>

              <div className="flex items-center gap-8 mt-14 animate-fade-up delay-400">
                {[
                  { value: '21', label: 'Areas scored' },
                  { value: '< 3min', label: 'To your shortlist' },
                  { value: 'Free', label: 'No account needed' },
                ].map(item => (
                  <div key={item.label}>
                    <p className="font-mono text-2xl font-semibold text-white">{item.value}</p>
                    <p className="text-xs text-white/30 mt-0.5">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Floating Product Preview */}
            <div className="hidden lg:block relative">
              <div className="relative w-full h-[480px]">
                {/* Burlington waterfront background image */}
                <div className="absolute inset-0 rounded-2xl overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/aq0pbyw8_carol-ann-wodehouse-nWmrfcrLloA-unsplash.jpg"
                    alt="Burlington waterfront trail"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-slate-900/30" />
                </div>
                <FloatingCard className="absolute top-8 left-0 right-8 animate-float" delay="0.4s">
                  <p className="text-[10px] text-white/40 uppercase tracking-widest mb-3">Your top matches</p>
                  <div className="space-y-3">
                    {[
                      { name: 'LaSalle', score: 91, tag: 'Best fit for you' },
                      { name: 'Alton Village', score: 84, tag: null },
                      { name: 'Orchard', score: 72, tag: null },
                    ].map(m => (
                      <div key={m.name} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                        <div className="flex items-center gap-3">
                          <ScoreRing score={m.score} size={36} />
                          <div>
                            <p className="text-sm text-white font-medium">{m.name}</p>
                            {m.tag && <span className="text-[10px] text-emerald-400 font-mono">{m.tag}</span>}
                          </div>
                        </div>
                        <span className="font-mono text-xs text-white/40">{m.score}/100</span>
                      </div>
                    ))}
                  </div>
                </FloatingCard>

                <FloatingCard className="absolute bottom-12 right-0 w-56" delay="0.6s">
                  <p className="text-[10px] text-white/40 uppercase tracking-widest mb-2">Why LaSalle fits</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-emerald-400"></span>
                      <span className="text-[11px] text-white/70">Strong schools nearby</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-emerald-400"></span>
                      <span className="text-[11px] text-white/70">Budget aligns for detached</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-amber-400"></span>
                      <span className="text-[11px] text-white/50">Older housing stock</span>
                    </div>
                  </div>
                </FloatingCard>

                <FloatingCard className="absolute bottom-32 left-4 w-44" delay="0.8s">
                  <p className="text-[10px] text-white/40 uppercase tracking-widest mb-2">Your budget</p>
                  <p className="font-mono text-base text-white font-medium">$900K – $1.3M</p>
                  <p className="text-[10px] text-emerald-400 mt-1">6 of 12 areas in range</p>
                </FloatingCard>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[hsl(210,20%,99%)] to-transparent z-10"></div>
      </section>

      {/* ─── Problem / Solution ─── */}
      <section className="py-20 md:py-28 relative" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <p className="text-sm text-slate-400 mb-4 animate-fade-up">The problem with house-hunting in Burlington</p>
            <h2 className="text-3xl md:text-4xl tracking-tight leading-[1.15] mb-5 animate-fade-up delay-100" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Most buyers spend weeks looking at homes in areas that <span className="italic">don't fit their budget or family needs</span>
            </h2>
            <p className="text-base text-slate-500 leading-relaxed animate-fade-up delay-200">
              Burlington isn't one market — it's eleven. Condos from $425K in the East to $4.5M waterfront estates in Shoreacres. School catchments, commute times, and neighbourhood feel vary dramatically. The right home in the wrong neighbourhood is still the wrong decision.
            </p>
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="py-24 md:py-36 mesh-gradient-section relative" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-lg mb-16">
            <span className="text-xs font-semibold tracking-widest uppercase text-emerald-600 mb-3 block">The Buyer Blueprint</span>
            <h2 className="text-4xl md:text-5xl leading-[1.1] tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
              From confused<br /><span className="italic">to clear</span>
            </h2>
            <p className="text-base text-slate-500 leading-relaxed">No account. No obligation. Just 6 structured questions — then your personalised shortlist.</p>
          </div>

          <div className="bento-grid grid-cols-1 md:grid-cols-3 rounded-2xl overflow-hidden">
            {[
              {
                icon: MapPin,
                step: '01',
                title: 'Tell us what matters',
                desc: 'Budget range, timeline, kids\' ages, school preferences, commute tolerance — the stuff that actually determines where you should be looking.',
                accent: 'bg-emerald-500',
              },
              {
                icon: TrendingUp,
                step: '02',
                title: 'Get your shortlist',
                desc: 'All 21 Burlington neighbourhoods scored 0–100 against your inputs. Top matches come with reasons why they fit — and honest tradeoffs where they don\'t.',
                accent: 'bg-blue-500',
              },
              {
                icon: Users,
                step: '03',
                title: 'Move forward on your terms',
                desc: 'Ready to buy? We\'ll connect you with a Burlington specialist who already knows your profile. Still exploring? We\'ll send you tailored insights over time.',
                accent: 'bg-orange-500',
              },
            ].map((item, i) => (
              <div key={i} className="p-8 md:p-10 group" data-testid={`step-card-${item.step}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-9 h-9 rounded-xl ${item.accent} flex items-center justify-center`}>
                    <item.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-mono text-xs text-slate-400">{item.step}</span>
                </div>
                <h3 className="text-xl font-medium tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  {item.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Trust row */}
          <div className="flex flex-wrap items-center gap-6 mt-10 justify-center">
            {[
              { icon: Clock, text: 'Takes under 3 minutes' },
              { icon: Shield, text: 'Your data stays private' },
              { icon: Zap, text: 'Instant results — no waiting' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                <item.icon className="w-3.5 h-3.5" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Social Proof: What Buyers Like You Found ─── */}
      <section className="py-24 md:py-32 relative" data-testid="social-proof-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-lg mb-14">
            <span className="text-xs font-semibold tracking-widest uppercase text-emerald-600 mb-3 block">Recent matches</span>
            <h2 className="text-4xl md:text-5xl leading-[1.1] tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
              What buyers like you <span className="italic">found</span>
            </h2>
            <p className="text-base text-slate-500 leading-relaxed">
              Real results from recent Burlington Buyer Blueprints. Names removed, insights kept.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                origin: 'Toronto',
                type: 'Family with 2 kids (ages 4, 8)',
                budget: '$900K – $1.2M',
                timeline: '3–6 months',
                priority: 'Strong schools, quiet streets',
                matches: [
                  { name: 'LaSalle', score: 88 },
                  { name: 'Orchard', score: 82 },
                ],
                quote: 'We had no idea LaSalle existed — it\'s exactly what we were looking for.',
                daysAgo: 3,
              },
              {
                origin: 'Mississauga',
                type: 'Young couple, first home',
                budget: '$550K – $750K',
                timeline: '0–3 months',
                priority: 'Commute-friendly, affordable',
                matches: [
                  { name: 'Burlington East', score: 91 },
                  { name: 'Tyandaga', score: 79 },
                ],
                quote: 'We were only looking at condos downtown. The Blueprint showed us townhomes in our budget in Burlington East.',
                daysAgo: 5,
              },
              {
                origin: 'Hamilton',
                type: 'Family with 1 kid (age 11)',
                budget: '$1.1M – $1.6M',
                timeline: '6–12 months',
                priority: 'Newer homes, parks & trails',
                matches: [
                  { name: 'Alton Village', score: 94 },
                  { name: 'Freeman', score: 85 },
                ],
                quote: 'Freeman wasn\'t on our radar at all. Bigger lots, newer builds, and still Burlington schools.',
                daysAgo: 8,
              },
            ].map((buyer, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-slate-200/80 p-6 flex flex-col card-interactive"
                data-testid={`social-proof-card-${i}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-slate-500">{buyer.origin[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Moving from {buyer.origin}</p>
                      <p className="text-[11px] text-slate-400">{buyer.type}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-mono text-slate-300">{buyer.daysAgo}d ago</span>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 pb-4 border-b border-slate-100">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest">Budget</p>
                    <p className="font-mono text-xs font-medium text-slate-700">{buyer.budget}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest">Timeline</p>
                    <p className="text-xs font-medium text-slate-700">{buyer.timeline}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest">Priorities</p>
                    <p className="text-xs font-medium text-slate-700">{buyer.priority}</p>
                  </div>
                </div>

                {/* Matches */}
                <div className="mb-4">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Top matches</p>
                  <div className="flex items-center gap-3">
                    {buyer.matches.map((m) => (
                      <div key={m.name} className="flex items-center gap-2 bg-slate-50 rounded-full px-3 py-1.5">
                        <div className={`w-2 h-2 rounded-full ${m.score >= 85 ? 'bg-emerald-500' : 'bg-blue-500'}`}></div>
                        <span className="text-xs font-medium text-slate-700">{m.name}</span>
                        <span className="font-mono text-[10px] text-slate-400">{m.score}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quote */}
                <div className="mt-auto pt-4 border-t border-slate-100">
                  <p className="text-[13px] text-slate-600 leading-relaxed italic">"{buyer.quote}"</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/blueprint" data-testid="social-proof-cta">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-7 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all">
                See your matches <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <p className="text-xs text-slate-400 mt-3">Takes under 3 minutes. Free, no account needed.</p>
          </div>
        </div>
      </section>

      {/* ─── Featured Neighbourhoods ─── */}
      {neighbourhoods.length > 0 && (
        <section className="py-24 md:py-36 relative" data-testid="featured-neighbourhoods">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-end justify-between mb-4">
              <div>
                <span className="text-xs font-semibold tracking-widest uppercase text-emerald-600 mb-3 block">Browse by area</span>
                <h2 className="text-4xl md:text-5xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  21 areas. Very different <span className="italic">price points.</span>
                </h2>
              </div>
              <Link to="/neighbourhoods" className="hidden md:flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors" data-testid="view-all-neighbourhoods">
                View all 21 <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <p className="text-base text-slate-500 max-w-xl mb-12 leading-relaxed">
              From $425K condos in Burlington East to $4.5M lakefront estates in Shoreacres. Where you look depends entirely on what you need. Here's a starting point.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {neighbourhoods.slice(0, 4).map((n) => (
                <Link
                  key={n.slug}
                  to={`/neighbourhoods/${n.slug}`}
                  className="group bg-white rounded-2xl p-6 card-interactive"
                  data-testid={`neighbourhood-card-${n.slug}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                    <span className="font-mono text-[10px] text-slate-400 uppercase tracking-widest">Burlington</span>
                  </div>
                  <h3 className="text-lg font-medium tracking-tight mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
                    {n.name}
                  </h3>
                  <p className="text-[13px] text-slate-500 leading-relaxed mb-4 line-clamp-2">{n.vibe_summary}</p>
                  {n.price_bands?.detached?.min > 0 && (
                    <div className="pt-3 border-t border-slate-100">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Detached from</p>
                      <p className="font-mono text-sm font-medium text-slate-900">
                        {formatPrice(n.price_bands.detached.min)}
                      </p>
                    </div>
                  )}
                </Link>
              ))}
            </div>

            <div className="md:hidden text-center mt-8">
              <Link to="/neighbourhoods" data-testid="view-all-neighbourhoods-mobile">
                <Button variant="outline" className="rounded-full px-6 text-sm">
                  View all 21 neighbourhoods <ArrowRight className="ml-2 w-3.5 h-3.5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ─── Final CTA ─── */}
      <section className="py-24 md:py-36" data-testid="inline-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="relative rounded-3xl overflow-hidden p-12 md:p-20">
            <div className="absolute inset-0">
              <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/swd0mu3s_shutterstock_2675683289.jpg" alt="Burlington neighbourhood" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-slate-900/80" />
            </div>
            <div className="relative z-10 max-w-xl">
              <h2 className="text-3xl md:text-5xl text-white tracking-tight leading-[1.1] mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
                You don't need more listings. You need the <span className="italic">right neighbourhood first.</span>
              </h2>
              <p className="text-base text-white/50 leading-relaxed mb-4">
                The biggest mistake Burlington buyers make? House-hunting before neighbourhood-matching. Budget alignment, school catchments, commute math — get these right first, and the home search gets 10x easier.
              </p>
              <p className="text-sm text-white/30 mb-8">
                The Buyer Blueprint is free, takes under 3 minutes, and doesn't require an account or a phone call. Just answers.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/blueprint" data-testid="inline-cta-blueprint">
                  <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-7 py-3 h-auto text-sm font-semibold hover:-translate-y-px transition-all">
                    Start my Blueprint <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/neighbourhoods">
                  <Button variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10 rounded-full px-6 py-3 h-auto text-sm">
                    Browse all 21 areas
                  </Button>
                </Link>
              </div>
            </div>

            <div className="hidden md:block absolute top-12 right-16 z-10">
              <div className="glass-card-dark rounded-xl p-4 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs text-white font-medium">6 questions, 3 minutes</p>
                    <p className="text-[10px] text-white/40">Then your personalised shortlist</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
